import { getDocument } from '@atlaskit/browser-apis';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { MarkdownModePlugin, MarkdownModeView } from '../markdownModePluginType';

import { markdownModePluginKey } from './main';

const isMac =
	typeof navigator !== 'undefined' &&
	new RegExp("Mac", "iu").test(
		(navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData
			?.platform ?? navigator.platform,
	);

/**
 * Returns true if the keyboard event matches the toggle shortcut:
 *   Mac:           Cmd + E   (metaKey)
 *   Windows/Linux: Ctrl + E  (ctrlKey)
 */
export const isToggleMarkdownViewShortcut = (e: KeyboardEvent): boolean => {
	const modifierMatch = isMac ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
	return modifierMatch && !e.altKey && !e.shiftKey && e.code === 'KeyE';
};

/**
 * Returns true if focus is within the given editor's DOM container.
 *
 * We use `dom.contains(activeElement)` rather than `editorView.hasFocus()`
 * because in source view the ProseMirror editor is hidden and unfocused — the
 * active element is the CodeMirror input inside the editor's wrapper DOM.
 * `dom.contains` covers both wysiwyg focus (ProseMirror contenteditable) and
 * source focus (CodeMirror), ensuring that when multiple markdown editors exist
 * on the same page only the one that actually has focus responds to the shortcut.
 */
export const isEditorFocused = (editorView: EditorView): boolean => {
	const activeElement = getDocument()?.activeElement ?? null;
	return activeElement !== null && editorView.dom.contains(activeElement);
};

/**
 * Returns a document-level keydown handler that toggles the markdown view
 * between 'wysiwyg' and 'source'. This must be used instead of a ProseMirror
 * keymap because in source view the PM editor is hidden and unfocused, so PM
 * keymaps never fire.
 */
export const buildGlobalKeydownHandler = (
	api: ExtractInjectionAPI<MarkdownModePlugin> | undefined,
	getEditorView: () => EditorView | undefined,
): ((e: KeyboardEvent) => void) => {
	return (e: KeyboardEvent) => {
		if (!isToggleMarkdownViewShortcut(e)) {
			return;
		}
		const editorView = getEditorView();
		if (!editorView || editorView.isDestroyed) {
			return;
		}

		// Guard: only the editor that currently has focus should respond.
		// This prevents all markdown editors on the page from toggling
		// simultaneously when the shortcut is pressed.
		if (!isEditorFocused(editorView)) {
			return;
		}

		const pluginState = markdownModePluginKey.getState(editorView.state);

		if (!pluginState?.isMarkdownMode) {
			return;
		}

		const currentView: MarkdownModeView = pluginState.view ?? 'wysiwyg';

		// Preview (split view) is currently disabled — do nothing when in that
		// state rather than silently cycling to another view.
		if (currentView === 'preview') {
			return;
		}

		e.preventDefault();

		// Cycle through available views. When split view is enabled in the
		// future, add 'preview' to this array.
		const viewCycle: MarkdownModeView[] = ['wysiwyg', 'source'];
		const currentIndex = viewCycle.indexOf(currentView);
		const nextView = viewCycle[(currentIndex + 1) % viewCycle.length];
		api?.markdownMode?.actions.setView(nextView);
	};
};
